import { generateMnemonic, validateMnemonic } from "@scure/bip39";
import { wordlist } from "@scure/bip39/wordlists/english.js";
import { privateKeyFromSeedWords } from "nostr-tools/nip06";

/**
 * Recovery phrase: the root of a member's key (PLAN.md 4.6). 12 English BIP-39 words encode
 * 128 bits of entropy; NIP-06 turns them into the secp256k1 secret at m/44'/1237'/0'/0/0.
 * Same phrase, same key, on any device, with no server.
 */
export const PHRASE_WORDS = 12;
export const NIP06_PATH = "m/44'/1237'/0'/0/0";

export type PhraseProblem =
  | { code: "WORD_COUNT"; count: number }
  | { code: "UNKNOWN_WORD"; index: number; word: string }
  | { code: "CHECKSUM" };

export class PhraseError extends Error {
  constructor(readonly problem: PhraseProblem) {
    super(describeProblem(problem));
    this.name = "PhraseError";
  }
}

function describeProblem(p: PhraseProblem): string {
  switch (p.code) {
    case "WORD_COUNT":
      return `a recovery phrase has ${PHRASE_WORDS} words, got ${p.count}`;
    case "UNKNOWN_WORD":
      return `word ${p.index + 1} ("${p.word}") is not in the recovery word list`;
    case "CHECKSUM":
      return "the words do not form a valid recovery phrase; check their order and spelling";
  }
}

export function generateRecoveryPhrase(): string {
  return generateMnemonic(wordlist, 128);
}

/** Lowercase, single spaces, NFKD as BIP-39 requires. Tolerates line breaks and extra spaces from paper. */
export function normalizePhrase(input: string): string {
  return input.normalize("NFKD").toLowerCase().trim().split(/\s+/).filter(Boolean).join(" ");
}

export function phraseWords(input: string): string[] {
  const normalized = normalizePhrase(input);
  return normalized ? normalized.split(" ") : [];
}

/** The first thing wrong with a typed phrase, or null if it is valid. */
export function phraseProblem(input: string): PhraseProblem | null {
  const words = phraseWords(input);
  if (words.length !== PHRASE_WORDS) return { code: "WORD_COUNT", count: words.length };
  const unknown = words.findIndex((w) => !wordlist.includes(w));
  if (unknown >= 0) return { code: "UNKNOWN_WORD", index: unknown, word: words[unknown] };
  if (!validateMnemonic(words.join(" "), wordlist)) return { code: "CHECKSUM" };
  return null;
}

export function isValidPhrase(input: string): boolean {
  return phraseProblem(input) === null;
}

/** NIP-06 derivation, account 0, no BIP-39 passphrase. Throws PhraseError for an invalid phrase. */
export function secretKeyFromPhrase(input: string): Uint8Array {
  const problem = phraseProblem(input);
  if (problem) throw new PhraseError(problem);
  return privateKeyFromSeedWords(normalizePhrase(input), undefined, 0);
}
